import { Component, Input } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AsyncDropdown } from 'src/app/models/async-dropdown';
import { SortObject } from 'src/app/models/sortObject';
import { LovApiService } from 'src/app/services/lov-api.service';
import { LoadingService } from 'src/app/services/shared/loading.service';
import { ToastService } from 'src/app/services/toast.service';
import { OrdersService } from 'src/app/user/dashboard/orders/service/orders.service';
import { checkForValidationErrors } from 'src/app/utils/checkForValidationErrors';
import { OrderUpdateModel } from './models/order-update';
import { OrderService } from './service/order.service';
@Component({
    selector: 'njo-batch-order-edit',
    templateUrl: './batch-order-edit.component.html',
    styleUrls: ['./batch-order-edit.component.scss'],
    providers: [OrderService],
    standalone: false
})
export class BatchOrderEditComponent {
  public form: UntypedFormGroup;
  @Input() selectedRows: Array<string> = [];

  // dropdowns
  public customer: AsyncDropdown[] = [];
  public customerLoading: boolean = false;
  public orderStatus: AsyncDropdown[] = [];
  public orderStatusLoading: boolean = false;
  public lob: AsyncDropdown[] = [];
  public lobLoading: boolean = false;
  public pillar: AsyncDropdown[] = [];
  public pillarLoading: boolean = false;
  public solution: AsyncDropdown[] = [];
  public solutionLoading: boolean = false;
  public owner: AsyncDropdown[] = [];
  public ownerLoading: boolean = false;

  tags: Array<string> = [];
  tagsSuggestion: Array<string> = [];
  disableForm: boolean = true;

  constructor(
    private readonly modalService: NgbModal,
    private readonly orderService: OrderService,
    private readonly toastService: ToastService,
    private readonly lovApiService: LovApiService,
    private readonly loadingService: LoadingService,
    private readonly ordersDashboardService: OrdersService
  ) {
    this.initSingleForm();
  }

  initSingleForm() {
    this.initDropdowns();
    this.form = new UntypedFormGroup({
      customer: new UntypedFormControl(),
      lob: new UntypedFormControl(),
      orderStatus: new UntypedFormControl(),
      owner: new UntypedFormControl(),
      pillar: new UntypedFormControl(),
      solution: new UntypedFormControl(),
      tags: new UntypedFormControl()
    });
  }

  initDropdowns() {
    this.tags = [];
    this.customer.push(new AsyncDropdown());
    this.orderStatus.push(new AsyncDropdown());
    this.lob.push(new AsyncDropdown());
    this.solution.push(new AsyncDropdown());
    this.pillar.push(new AsyncDropdown());
  }

  open(content) {
    this.form.reset();
    this.modalService.open(content, { ariaLabelledBy: 'modal-basic-title' }).result.then(
      (result) => {},
      (reason) => {
        this.initSingleForm();
      }
    );
  }

  close() {
    this.initSingleForm();
    this.modalService.dismissAll();
  }

  async bulkUpdateOrders(e) {
    for (let i = 0; i < this.selectedRows.length; i++) {
      this.toastService.showInfo("Updating order number " + Number(i + 1));
      this.loadingService.pushLoadingRequests('any');
      if (i !== 0) await new Promise((r) => setTimeout(r, 2000));
      this.orderService
        .bulkUpdateOrder(
          Object.assign(new OrderUpdateModel(), {
            identifier: this.selectedRows[i],
            customer: this.form.value.customer,
            lob: this.form.value.lob,
            orderStatus: this.form.value.orderStatus,
            owner: this.form.value.owner,
            pillar: this.form.value.pillar,
            solution: this.form.value.solution,
            tags: this.tags,

          })
        )
        .then((response) => {
          this.loadingService.popLoadingRequests();
          if (i === this.selectedRows.length - 1) {
            this.close();
            this.ordersDashboardService.fetchOrdersDashboard(true);
            this.toastService.showSuccess('Operation Success! ' + this.selectedRows.length + ' orders updated!');
            this.selectedRows = [];
          }
        })
        .catch((error) => {
          checkForValidationErrors(error, this.form);
          this.loadingService.popLoadingRequests();
        });
    }
  }

  clearAll() {
    this.initSingleForm();
  }

  async fetchCustomer() {
    this.customer = [];
    this.customerLoading = true;
    let sort: SortObject[] = [{ prop: 'name', dir: 'asc' }];
    this.customer = await this.lovApiService.getDropdownValues('order', 'customer', sort);
    this.customerLoading = false;
  }

  async fetchOrderStatus() {
    this.orderStatus = [];
    this.orderStatusLoading = true;
    let sort: SortObject[] = [{ prop: 'name', dir: 'asc' }];
    this.orderStatus = await this.lovApiService.getDropdownValues('order', 'status', sort);
    this.orderStatusLoading = false;
  }

  async fetchLob() {
    this.lob = [];
    this.lobLoading = true;
    let sort: SortObject[] = [{ prop: 'name', dir: 'asc' }];
    this.lob = await this.lovApiService.getDropdownValues('order', 'lob', sort);
    this.lobLoading = false;
  }

  async fetchPillar() {
    this.pillar = [];
    this.pillarLoading = true;
    let sort: SortObject[] = [{ prop: 'name', dir: 'asc' }];
    this.pillar = await this.lovApiService.getDropdownValues('order', 'pillar', sort);
    this.pillarLoading = false;
  }

  async fetchSolution() {
    this.solution = [];
    this.solutionLoading = true;
    let sort: SortObject[] = [{ prop: 'name', dir: 'asc' }];
    this.solution = await this.lovApiService.getDropdownValues('order', 'solution', sort);
    this.solutionLoading = false;
  }

  async fetchOwner() {
    this.owner = [];
    this.ownerLoading = true;
    let sort: SortObject[] = [{ prop: 'email', dir: 'asc' }];
    this.owner = await this.lovApiService.getDropdownValues('order', 'owner', sort);
    this.ownerLoading = false;
  }

  getTags = (text: string): Observable<any> => {
    if (text.length > 0) {
      return this.orderService.getTags(text.toLowerCase()).pipe(
        map((tagsSuggestion) =>
          tagsSuggestion.map((item: any) => {
            return item.value + ' (' + item.count + ')';
          })
        )
      );
    }
  };

  public transform(value): Observable<object> {
    let item;
    if (value?.display) {
      let formattedValue = value.display.split(' (')[0];
      item = { display: `${formattedValue}`, value: `${formattedValue}` };
    } else {
      item = { display: value, value: value };
    }
    return of(item);
  }

  checkValid() {
    this.form.markAsDirty();
    if(this.tags.length == 0) {
      this.disableForm = true;
      Object.keys(this.form.controls).forEach(key => {
        if (this.form.get(key).value !== null) {
          this.disableForm = false;
        }
      });
    }
  }

}
